import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import CloseIcon from "@material-ui/icons/Close";
import StorefrontIcon from "@material-ui/icons/Storefront";

import { endPoint, graphqlUrl } from "utils/config";
import sparkLogo from "assets/img/sparkLogo.svg";
import DialogShell from "designV2/components/DialogShell";
import "./shopNav.css";

// Small, cheap, and genuinely actionable: how many support tickets are
// still waiting on a first reply. Polls rather than subscribes since the
// sidebar is mounted for the whole session and a subscription per tab
// would be overkill for a number that only needs to be roughly current.
const GET_UNREPLIED_COUNT = gql`
  query getUnrepliedTicketsCountForSidebar {
    getConversationsCount(filters: { repliedByAdmin: false, closed: false })
  }
`;

// The sidebar used to only ever say "Spark" — with more than one shop
// open across tabs/sessions (or just after a while away from the
// dashboard) there was no way to tell which shop you were actually
// looking at without clicking into Edit Shop.
const GET_SHOP_IDENTITY = gql`
  query getShopIdentityForSidebar {
    getShopByShopAdmin {
      _id
      name
      logoUrl
      active
    }
  }
`;

const onLogout = () => {
  window.localStorage.removeItem("shopId");
  window.localStorage.removeItem("refreshToken");
  window.localStorage.removeItem("token");
  window.localStorage.removeItem("shopType");
  window.localStorage.removeItem("verificationCode");
  window.localStorage.removeItem("phoneNumber");
  window.localStorage.removeItem("permissions");
  window.location.href = endPoint + "/login";
};

/**
 * ShopSidebar - the shop admin's left navigation.
 *
 * Reads the same `routes` array the app already builds in routes.js (so
 * the Restaurant/Purchase-specific labels — "Foods" vs "Products",
 * "Menu" vs "Categories" — keep working without duplicating that logic
 * here), and renders `collapse` groups (Orders, Finance) as small
 * always-open sections rather than an expand/collapse control: with
 * only two children each, a toggle was one more click to find "Orders
 * history" for no real benefit.
 */
const ShopSidebar = ({ routes, mobileOpen, onCloseMobile }) => {
  const [logoutOpen, setLogoutOpen] = useState(false);
  const { data: unrepliedData } = useQuery(GET_UNREPLIED_COUNT, {
    pollInterval: 60000,
    fetchPolicy: "cache-and-network",
  });
  const unrepliedCount = (unrepliedData && unrepliedData.getConversationsCount) || 0;

  const { data: shopData } = useQuery(GET_SHOP_IDENTITY);
  const shop = shopData && shopData.getShopByShopAdmin;

  const renderLink = (route, key) => (
    <NavLink
      key={key}
      to={route.layout + route.path}
      className="shopNavLink"
      activeClassName="isActive"
      onClick={() => {
        if (window.innerWidth < 960) onCloseMobile();
      }}
    >
      {route.icon && <route.icon className="shopNavLinkIcon" fontSize="small" />}
      <span className="shopNavLinkLabel">{route.name}</span>
      {route.path === "/tickets" && unrepliedCount > 0 && (
        <span className="shopNavBadge">{unrepliedCount > 99 ? "99+" : unrepliedCount}</span>
      )}
    </NavLink>
  );

  const content = (
    <React.Fragment>
      <NavLink to="/shopAdmin/dashboard" className="shopNavBrand">
        <img src={sparkLogo} alt="Spark" />
        <span>Spark</span>
      </NavLink>

      {shop && (
        <NavLink
          to={`/shopAdmin${window.localStorage.getItem("shopType") === "Restaurant" ? "/editRestaurant" : "/editShop"}`}
          className="shopNavWorkspace"
        >
          <span className="shopNavWorkspaceLogo">
            {shop.logoUrl ? (
              <img src={`${graphqlUrl}${shop.logoUrl}`} alt="" />
            ) : (
              <StorefrontIcon fontSize="small" />
            )}
          </span>
          <span className="shopNavWorkspaceText">
            <span className="shopNavWorkspaceName">{shop.name}</span>
            <span className={`shopNavWorkspaceStatus ${shop.active ? "isOpen" : "isClosed"}`}>
              <span className="shopNavWorkspaceDot" />
              {shop.active ? "Open" : "Closed"}
            </span>
          </span>
        </NavLink>
      )}

      <nav className="shopNavList">
        {routes.map((route, index) => {
          if (route.redirect) return null;
          if (route.collapse) {
            return (
              <div className="shopNavGroup" key={index}>
                <div className="shopNavGroupLabel">{route.name}</div>
                {route.views.map((child, childIndex) => renderLink(child, `${index}-${childIndex}`))}
              </div>
            );
          }
          return renderLink(route, index);
        })}
      </nav>

      <button type="button" className="shopNavLogout" onClick={() => setLogoutOpen(true)}>
        <ExitToAppIcon fontSize="small" />
        <span>Log out</span>
      </button>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <aside className="shopSidebar shopSidebarDesktop">{content}</aside>

      <div className={`shopSidebarBackdrop ${mobileOpen ? "isOpen" : ""}`} onClick={onCloseMobile} />
      <aside className={`shopSidebar shopSidebarMobile ${mobileOpen ? "isOpen" : ""}`}>
        <button type="button" className="shopSidebarCloseButton" onClick={onCloseMobile} aria-label="Close menu">
          <CloseIcon fontSize="small" />
        </button>
        {content}
      </aside>

      <DialogShell
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        title="Log out"
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setLogoutOpen(false) },
          { label: "Log out", onClick: onLogout, variant: "danger" },
        ]}
      >
        <div className="shopNavLogoutConfirm">Do you want to log out?</div>
      </DialogShell>
    </React.Fragment>
  );
};

export default ShopSidebar;
